import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Tag, Search, User, ChevronRight } from 'lucide-react';
import { useTemplate } from '@/context/TemplateContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { BlogPost } from '@/config/templates';
import { PageHero } from '@/components/common/PageHero';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { PageSidebar } from '@/components/common/PageSidebar';
import { SEOHead } from '@/components/seo/SEOHead';
import { usePageDesign } from '@/hooks/usePageDesign';

const categoryLabels: Record<string, string> = {
  'maintenance': 'Maintenance',
  'cost-guides': 'Cost Guides',
  'how-to': 'How-To',
  'hiring-tips': 'Hiring Tips',
};

// Generate 12 placeholder articles based on template
const generatePlaceholderArticles = (blogPosts: BlogPost[]): BlogPost[] => {
  const placeholders: BlogPost[] = [];
  const categories: BlogPost['category'][] = ['maintenance', 'cost-guides', 'how-to', 'hiring-tips'];
  
  // Add the real blog posts first
  placeholders.push(...blogPosts);
  
  // Fill up to 12
  for (let i = blogPosts.length; i < 12; i++) {
    const category = categories[i % 4];
    placeholders.push({
      id: `placeholder-${i}`,
      title: `${categoryLabels[category]} Article ${i + 1}`,
      excerpt: 'This is a placeholder article demonstrating the resource section layout and functionality.',
      category,
      date: new Date(2024, 0, 20 - i).toISOString().split('T')[0],
      readTime: `${3 + (i % 5)} min`,
    });
  }
  
  return placeholders;
};

// Article Card Component
const ArticleCard: React.FC<{ article: BlogPost; index: number }> = ({ article, index }) => {
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      viewport={{ once: true }}
    >
      <Link
        to={`/resources/${article.id}`}
        className="group block bg-card rounded-xl border border-border overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-300"
      >
        {/* Image placeholder */}
        <div className="aspect-video bg-muted relative overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/20">
            <Tag className="h-8 w-8 text-primary/50" />
          </div>
          <div className="absolute top-3 left-3">
            <span className="px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
              {categoryLabels[article.category]}
            </span>
          </div>
        </div>
        
        <div className="p-5">
          <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-primary transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{article.excerpt}</p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(article.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {article.readTime}
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
};

// Resources Index Page
export const ResourcesPage: React.FC = () => {
  const { template, isTransitioning } = useTemplate();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { heroStyle, showBreadcrumbs, showTrustBadges, showCtaButtons } = usePageDesign('blog_roll');
  
  const articles = generatePlaceholderArticles(template.blogPosts);
  const brandName = template.brand?.name || 'Our Company';
  
  const filteredArticles = articles.filter(article => {
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Popular articles for sidebar
  const popularLinks = articles.slice(0, 4).map(article => ({
    title: article.title,
    href: `/resources/${article.id}`,
  }));

  return (
    <>
      <SEOHead
        title={`Resources & Expert Insights | ${brandName}`}
        description={`Helpful guides, tips, and industry insights from ${brandName}. Learn about maintenance, costs, and hiring tips to make informed decisions.`}
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isTransitioning ? 0.5 : 1 }}
        transition={{ duration: 0.3 }}
      >
        <ConfigurableHero
          heroStyle={heroStyle}
          title="Resources & Insights"
          subtitle="Helpful guides, tips, and industry insights to help you make informed decisions about your home projects."
          badge="Expert Advice"
          showBreadcrumbs={showBreadcrumbs}
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Resources' }]}
          showTrustBadges={showTrustBadges}
          showCtaButtons={showCtaButtons}
        />

        {/* Filters */}
        <section className="py-6 border-b border-border sticky top-16 lg:top-[105px] bg-background/95 backdrop-blur-sm z-30">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Categories */}
              <nav className="flex flex-wrap gap-2" aria-label="Article categories">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={cn(
                    'px-4 py-2 rounded-full text-sm font-medium transition-colors',
                    selectedCategory === 'all'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80'
                  )}
                  aria-pressed={selectedCategory === 'all'}
                >
                  All
                </button>
                {Object.entries(categoryLabels).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedCategory(key)}
                    className={cn(
                      'px-4 py-2 rounded-full text-sm font-medium transition-colors',
                      selectedCategory === key
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/80'
                    )}
                    aria-pressed={selectedCategory === key}
                  >
                    {label}
                  </button>
                ))}
              </nav>

              {/* Search */}
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  aria-label="Search articles"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Articles Grid with Sidebar */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Main Content */}
              <main className="lg:col-span-3" role="main">
                {filteredArticles.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredArticles.map((article, index) => (
                      <ArticleCard key={article.id} article={article} index={index} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No articles found matching your criteria.</p>
                    <Button
                      variant="ghost"
                      className="mt-4"
                      onClick={() => { setSelectedCategory('all'); setSearchQuery(''); }}
                    >
                      Clear filters
                    </Button>
                  </div>
                )}
              </main>

              {/* Sidebar */}
              <aside className="lg:col-span-1">
                <div className="sticky top-40">
                  <PageSidebar 
                    showCta
                    showTrustBadges={false}
                    showPromo
                    relatedLinks={popularLinks}
                    relatedTitle="Popular Articles"
                    ctaTitle="Have Questions?"
                    ctaDescription="Our team is ready to help with any questions about your project."
                  />
                </div>
              </aside>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

// Single Article Page
export const ArticlePage: React.FC = () => {
  const { template, isTransitioning } = useTemplate();
  const { articleId } = useParams();
  const navigate = useNavigate();
  
  const articles = generatePlaceholderArticles(template.blogPosts);
  const article = articles.find(a => a.id === articleId);
  const brandName = template.brand?.name || 'Our Company';
  
  if (!article) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <Button onClick={() => navigate('/resources')}>Back to Resources</Button>
        </div>
      </div>
    );
  }

  // Get related articles (same category, excluding current)
  const relatedArticles = articles
    .filter(a => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const relatedLinks = relatedArticles.map(a => ({
    title: a.title,
    href: `/resources/${a.id}`,
  }));

  return (
    <>
      <SEOHead
        title={`${article.title} | ${brandName}`}
        description={article.excerpt}
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isTransitioning ? 0.5 : 1 }}
        transition={{ duration: 0.3 }}
        className="pt-24"
      >
        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Main Content */}
              <main className="lg:col-span-3">
                {/* Back link */}
                <Link 
                  to="/resources" 
                  className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Resources
                </Link>

                {/* Header */}
                <header className="mb-8">
                  <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4">
                    {categoryLabels[article.category]}
                  </span>
                  <h1 className="font-heading text-3xl lg:text-4xl font-bold mb-4">{article.title}</h1>
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="h-4 w-4" />
                      <time dateTime={article.date}>
                        {new Date(article.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </time>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="h-4 w-4" />
                      {article.readTime} read
                    </span>
                    <span className="flex items-center gap-1.5">
                      <User className="h-4 w-4" />
                      {brandName} Team
                    </span>
                  </div>
                </header>

                {/* Featured image placeholder */}
                <div className="aspect-video bg-muted rounded-xl mb-8 flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <Tag className="h-12 w-12 mx-auto mb-2" />
                    <p>Featured Image</p>
                  </div>
                </div>

                {/* Content */}
                <div className="prose prose-lg max-w-none">
                  <p className="lead text-lg text-muted-foreground">{article.excerpt}</p>
                  
                  <h2>Introduction</h2>
                  <p>
                    When it comes to home improvement projects, having the right information is crucial. 
                    This guide will walk you through everything you need to know about this topic, 
                    from basic concepts to advanced strategies.
                  </p>
                  
                  <h2>Key Considerations</h2>
                  <p>
                    Before starting any project, it's important to understand the scope, budget, and timeline. 
                    Working with a professional can help ensure your project is completed correctly and efficiently.
                  </p>
                  
                  <ul>
                    <li>Always get multiple quotes before making a decision</li>
                    <li>Verify licenses and insurance</li>
                    <li>Ask for references and check reviews</li>
                    <li>Get everything in writing</li>
                  </ul>
                  
                  <h2>Common Questions</h2>
                  <p>
                    Many homeowners have questions about the best approach for their specific situation. 
                    Our team at {brandName} is always happy to provide guidance and answer any questions you may have.
                  </p>
                  
                  <h2>Next Steps</h2>
                  <p>
                    Ready to get started? Contact {brandName} today for a free consultation. 
                    Our experienced team is here to help with all your project needs.
                  </p>
                </div>

                {/* CTA */}
                <div className="mt-12 p-8 bg-primary text-primary-foreground rounded-xl text-center">
                  <h3 className="font-heading text-2xl font-bold mb-2">Have Questions?</h3>
                  <p className="text-primary-foreground/80 mb-6">
                    Our team is ready to help with your project.
                  </p>
                  <Button asChild variant="secondary">
                    <Link to="/contact">Contact Us Today</Link>
                  </Button>
                </div>
              </main>

              {/* Sidebar */}
              <aside className="lg:col-span-1">
                <div className="sticky top-28">
                  <PageSidebar 
                    showCta
                    showTrustBadges
                    showPromo={false}
                    relatedLinks={relatedLinks}
                    relatedTitle="Related Articles"
                  />
                </div>
              </aside>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-16 bg-muted" aria-labelledby="related-heading">
            <div className="container mx-auto px-4">
              <h2 id="related-heading" className="font-heading text-2xl font-bold mb-8 text-center">
                Related Articles
              </h2>
              <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {relatedArticles.map((related, index) => (
                  <ArticleCard key={related.id} article={related} index={index} />
                ))}
              </div>
            </div>
          </section>
        )}
      </motion.div>
    </>
  );
};
